<template>
  <q-stepper ref="newTeamStepper" class="new-team-stepper">
    <!-- Step 1 -->
    <q-step title="" class="q-px-lg" default>
      <div class="text-center">
        <span class="block q-headline q-mb-sm q-mt-lg text-dark">{{ $t('new_team_stepper.step_1.title') }}</span>
        <span class="q-title text-light">{{ $t('new_team_stepper.step_1.subtitle') }}</span>
      </div>

      <div class="q-mt-xl">
        <legal-form-selector
          name="legalForm"
          v-model="formData.legalForm"
          v-validate.disable="'required'"
        />

        <div class="q-mt-md text-center">
          <span v-if="errors.has('legalForm')" class="text-negative">{{ errors.first('legalForm') }}</span>
        </div>
      </div>

      <q-stepper-navigation class="justify-end">
        <q-btn v-if="hasTeam" flat text-color="grey-7" class="on-left" @click="handleCancel">{{ $t('cancel') }}</q-btn>
        <q-btn color="teal" @click="handleLegalFormNext">{{ $t('next') }}</q-btn>
      </q-stepper-navigation>
    </q-step>
    <!-- /Step 1 -->

    <!-- Step 2 -->
    <q-step title="" class="q-px-lg">
      <div class="text-center">
        <span class="block q-headline q-mb-sm q-mt-lg text-dark">{{ $t('new_team_stepper.step_2.title') }}</span>
        <span class="q-title text-light">{{ $t('new_team_stepper.step_2.subtitle') }}</span>
      </div>

      <div class="q-mt-xl">
        <small-business-selector
          name="smallBusiness"
          v-model="formData.isSmallBusiness"
          v-validate.disable="'required'"
        />

        <div class="q-mt-md text-center">
          <span v-if="errors.has('smallBusiness')" class="text-negative">{{ errors.first('smallBusiness') }}</span>
        </div>
      </div>

      <q-stepper-navigation class="justify-end">
        <q-btn v-if="hasTeam" flat text-color="grey-7" class="on-left" @click="handleCancel">{{ $t('cancel') }}</q-btn>
        <q-btn flat color="teal" class="on-left" @click="handlePrevious">{{ $t('back') }}</q-btn>
        <q-btn color="teal" @click="handleLegalFormNext">{{ $t('next') }}</q-btn>
      </q-stepper-navigation>
    </q-step>
    <!-- /Step 2 -->

    <!-- Step 3 -->
    <q-step title="" class="q-px-lg">
      <div class="text-center">
        <span class="block q-headline q-mb-sm q-mt-lg text-dark">{{ $t('new_team_stepper.step_3.title') }}</span>
        <span class="q-title text-light">{{ $t('new_team_stepper.step_3.subtitle') }}</span>
      </div>

      <div class="q-mt-xl">
        <!-- Team Name -->
        <q-field
          :label="$t('team_name')"
          :labelWidth="3"
          :error="errors.has('teamName')"
          :error-label="errors.first('teamName')"
        >
          <q-input
            v-model="formData.name"
            v-validate.disable="'required|min:1|max:255'"
            name="teamName"
            :placeholder="$t('team_name')"
          />
        </q-field>

        <address-input v-model="formData.address" required expanded :labelWidth="3"/>
      </div>

      <q-stepper-navigation class="justify-end">
        <q-btn v-if="hasTeam" flat text-color="grey-7" class="on-left" @click="handleCancel">{{ $t('cancel') }}</q-btn>
        <q-btn flat color="teal" class="on-left" @click="handlePrevious">{{ $t('back') }}</q-btn>
        <q-btn color="teal" @click="handleSubmit">{{ $t('save') }}</q-btn>
      </q-stepper-navigation>
    </q-step>
    <!-- /Step 3 -->

    <q-inner-loading :visible="loading"/>
  </q-stepper>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import AddressInput from 'components/Common/Addressing/AddressInput.vue'
  import CurrencyInput from 'components/Common/Inputs/CurrencyInput.vue'
  import CustomerAddress from 'src/utils/SubResources/CustomerAddress'
  import LanguageInput from 'components/Common/Inputs/LanguageInput.vue'
  import LegalFormSelector from './LegalFormSelector.vue'
  import SmallBusinessSelector from './SmallBusinessSelector.vue'
  import changeCase from 'change-case-object'
  import { createErrorFromResponse } from 'src/utils/error'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        formData: {
          name: '',
          legalForm: null,
          isSmallBusiness: null,
          address: CustomerAddress.empty()
        },
        loading: false
      }
    },

    computed: {
      ...mapGetters(['locale', 'teams', 'activeTeam']),

      hasTeam () {
        return !!this.teams.length
      }
    },

    methods: {
      ...mapActions(['createTeam']),

      handleCancel () {
        this.$router.push({ name: 'dashboard', params: { teamId: this.activeTeam.id } })
      },

      handlePrevious () {
        this.$refs.newTeamStepper.previous()
      },

      handleLegalFormNext () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              return false
            }

            this.$refs.newTeamStepper.next()
          })
      },

      handleSubmit () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              return false
            }

            this.loading = true

            return this.handleCreateTeam()
          })
      },

      handleCreateTeam () {
        return this.createTeam({
          name: this.formData.name,
          language: 'de',
          legal_form: this.formData.legalForm,
          is_small_business: this.formData.isSmallBusiness,
          address: changeCase.snake(this.formData.address)
        })
          .then(team => {
            this.$router.push({ name: 'dashboard', params: { teamId: team.id } })
          })
          .catch(error => {
            this.loading = false

            createErrorFromResponse(error)
          })
      }
    },

    components: {
      AddressInput,
      CurrencyInput,
      LanguageInput,
      LegalFormSelector,
      SmallBusinessSelector
    }
  }
</script>

<style lang="stylus">
  @import '~variables'

  .new-team-stepper
    .q-stepper-header
      display: none

  .heroicon
    user-select: none
  .heroicon-outline
    fill: #626262
  .heroicon-shadows
    fill: #626262
    opacity: 0.4
  .heroicon-component-fill
    fill: white
  .heroicon-component-accent
    fill: $teal-4
</style>
